import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Popover,
  Button,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  styled,
  tableCellClasses,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import { toast } from "react-toastify";
import { isMobile } from "../../globalStyles/mobile";
import Pagination from "../pagination";
import FilterModal from "../filterModal";
import TableHeader from "../table-header";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { formatDate } from "../../utils/formatDate";
import { transferOrderService } from "../../utils/services/transferOrderService";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
}));

/**
 * Status pill colours, grouped by what Pipe17's status means: done (green),
 * moving (blue), waiting on someone (amber), stopped (red), the rest neutral.
 * The label itself is Pipe17's value, unchanged.
 */
const STATUS_GROUP = {
  received: "done",
  fulfilled: "done",
  closed: "done",
  archived: "done",
  inTransit: "moving",
  sentToFulfillment: "moving",
  partialFulfillment: "moving",
  partialReceived: "moving",
  readyForFulfillment: "moving",
  new: "waiting",
  draft: "waiting",
  onHold: "waiting",
  toBeValidated: "waiting",
  reviewRequired: "waiting",
  canceled: "stopped",
  returned: "stopped",
  refunded: "stopped",
};
const GROUP_COLORS = {
  done: { bg: "#ECFDF3", fg: "#027A48" },
  moving: { bg: "#EFF8FF", fg: "#175CD3" },
  waiting: { bg: "#FFFAEB", fg: "#B54708" },
  stopped: { bg: "#FEF3F2", fg: "#B42318" },
  neutral: { bg: "#F2F4F7", fg: "#344054" },
};

const StatusChip = ({ status }) => {
  const color = GROUP_COLORS[STATUS_GROUP[status] || "neutral"];
  return (
    <Chip
      label={status || "--"}
      size="small"
      sx={{
        backgroundColor: color.bg,
        color: color.fg,
        fontFamily: "Poppins",
        fontSize: "11px",
        fontWeight: 500,
        height: "22px",
      }}
    />
  );
};

const cellText = (value) => (
  <Typography fontSize="12px" color="var(--text-secondary)" fontWeight={400} fontFamily={"Poppins"}>
    {value === 0 ? 0 : value || "--"}
  </Typography>
);

const dateText = (d) => (d ? formatDate(d, "") : null);
const locationText = (name, id) => name || id || null;
const clientName = (c) => (c && typeof c === "object" ? c.business_name || c.name : null);

/** Full transfer order: header details and every line. */
const TransferOrderViewModal = ({ open, onClose, transfer }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !transfer?._id) return;
    setLoading(true);
    setDetail(null);
    transferOrderService
      .fetchById(transfer._id)
      .then(setDetail)
      .catch(() => {
        setDetail(transfer);
        toast.error("Could not load the full transfer order.");
      })
      .finally(() => setLoading(false));
  }, [open, transfer]);

  const t = detail || transfer || {};
  const fields = [
    ["Transfer", t.ext_order_id || t.external_id],
    ["Pipe17 transfer id", t.external_id],
    ["Status", <StatusChip status={t.status} key="s" />],
    ["From", locationText(t.from_location_name, t.from_location_id)],
    ["From warehouse", t.from_warehouse?.name],
    ["To", locationText(t.to_location_name, t.to_location_id)],
    ["To warehouse", t.to_warehouse?.name],
    ["Expected ship", dateText(t.expected_ship_date)],
    ["Expected arrival", dateText(t.expected_arrival_date)],
    ["Actual arrival", dateText(t.actual_arrival_date)],
    ["Carrier", [t.shipping_carrier, t.shipping_service].filter(Boolean).join(" · ")],
    ["Created by", t.employee_name],
    ["Created in Pipe17", dateText(t.provider_created_at)],
    ["Updated in Pipe17", dateText(t.provider_updated_at)],
    ["Last synced", dateText(t.last_synced_at)],
    ["Notes", [t.transfer_notes, t.shipping_notes].filter(Boolean).join(" — ")],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, fontFamily: "Poppins", fontWeight: 600 }}>
        Transfer order {t.ext_order_id || t.external_id || ""}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {(t.exceptions || []).length > 0 && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", color: "#B54708" }}>
                <WarningAmberIcon fontSize="small" />
                <Typography fontSize="13px" fontFamily="Poppins">
                  Pipe17 exceptions: {t.exceptions.join(", ")}
                </Typography>
              </Box>
            )}
            <Grid container spacing={1.5}>
              {fields.map(([label, value]) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <Typography fontSize="11px" color="var(--text-secondary)" fontFamily="Poppins" textTransform="uppercase">
                    {label}
                  </Typography>
                  <Box sx={{ fontSize: "13px", fontFamily: "Poppins", color: "var(--text-primary)" }}>
                    {value === 0 ? 0 : value || "--"}
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box>
              <Typography fontSize="14px" fontWeight={600} fontFamily="Poppins" mb={1}>
                Lines ({t.line_count || 0}) · {t.total_quantity || 0} units
              </Typography>
              <TableContainer sx={{ border: "1px solid var(--border-color)", borderRadius: "6px" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["SKU", "Product", "Quantity", "Shipped", "Received"].map((h) => (
                        <StyledTableCell key={h}>
                          <Typography fontSize="11px" fontWeight={600} fontFamily="Poppins">
                            {h}
                          </Typography>
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(t.line_items || []).length === 0 ? (
                      <TableRow>
                        <StyledTableCell colSpan={5}>{cellText("No lines")}</StyledTableCell>
                      </TableRow>
                    ) : (
                      t.line_items.map((l, i) => (
                        <StyledTableRow key={`${l.sku}-${i}`}>
                          <StyledTableCell>{cellText(l.sku)}</StyledTableCell>
                          <StyledTableCell>{cellText(l.product?.name || l.name)}</StyledTableCell>
                          <StyledTableCell>{cellText(l.quantity)}</StyledTableCell>
                          <StyledTableCell>{cellText(l.shipped_quantity)}</StyledTableCell>
                          <StyledTableCell>{cellText(l.received_quantity)}</StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TransferOrders = ({
  data,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  onChangeFilter,
  filterData,
  onSync,
  syncing,
  syncDisabledReason,
  showClient,
}) => {
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElPopover, setAnchorElPopover] = useState(null);
  const [selectedfilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
  });

  const startIndex = page * (filterData?.limit || 15);

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key === filterKey));
  };
  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };
  const handleSaveFilter = (selectedOptions, filterKey, sortOrder, searchKey) => {
    onChangeFilter({ key: filterKey, filter: selectedOptions, sort: sortOrder, searchKey });
    handleCloseFilter();
  };
  const handleView = (item) => {
    setSelected(item);
    setOpenViewModal(true);
    setAnchorElPopover(null);
  };

  const TableSchema = [
    { key: "sl_no", title: "SL NO", width: "4%", render: (item, index) => cellText(startIndex + index + 1) },
    ...(showClient ? [{ key: "client", title: "CLIENT", render: (x) => cellText(clientName(x?.client_id)) }] : []),
    {
      key: "external_id",
      title: "TRANSFER",
      filterKey: "external_id",
      handleOpenFilter,
      render: (x) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography fontSize="12px" color="var(--text-primary)" fontWeight={500} fontFamily={"Poppins"}>
            {x?.ext_order_id || x?.external_id || "--"}
          </Typography>
          {x?.exceptions?.length ? (
            <Tooltip title={x.exceptions.join(", ")}>
              <WarningAmberIcon sx={{ fontSize: 14, color: "#B54708" }} />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      filterKey: "status",
      handleOpenFilter,
      render: (x) => <StatusChip status={x?.status} />,
    },
    {
      key: "from_location_name",
      title: "FROM",
      filterKey: "from_location_name",
      handleOpenFilter,
      render: (x) => cellText(locationText(x?.from_location_name, x?.from_location_id)),
    },
    {
      key: "to_location_name",
      title: "TO",
      filterKey: "to_location_name",
      handleOpenFilter,
      render: (x) => cellText(locationText(x?.to_location_name, x?.to_location_id)),
    },
    { key: "line_count", title: "LINES", width: "5%", render: (x) => cellText(x?.line_count) },
    { key: "total_quantity", title: "UNITS", width: "5%", render: (x) => cellText(x?.total_quantity) },
    { key: "total_received", title: "RECEIVED", width: "6%", render: (x) => cellText(x?.total_received) },
    { key: "expected_ship_date", title: "EXPECTED SHIP", render: (x) => cellText(dateText(x?.expected_ship_date)) },
    {
      key: "expected_arrival_date",
      title: "EXPECTED ARRIVAL",
      filterKey: "expected_arrival_date",
      handleOpenFilter,
      render: (x) => cellText(dateText(x?.expected_arrival_date)),
    },
    { key: "provider_updated_at", title: "UPDATED", render: (x) => cellText(dateText(x?.provider_updated_at)) },
    {
      key: "action",
      title: "",
      width: "3%",
      render: (x) => (
        <IconButton
          aria-label="more actions"
          onClick={(e) => {
            setAnchorElPopover(e.currentTarget);
            setSelected(x);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const MobileCardView = () => (
    <Box>
      {data.map((x, index) => (
        <Card key={x?._id || index} sx={{ marginBottom: 2, backgroundColor: "var(--bg-paper)", borderRadius: 4 }}>
          <CardContent>
            <Grid container spacing={1}>
              {[
                ...(showClient ? [["Client", clientName(x?.client_id)]] : []),
                ["Transfer", x?.ext_order_id || x?.external_id],
                ["Status", x?.status],
                ["From", locationText(x?.from_location_name, x?.from_location_id)],
                ["To", locationText(x?.to_location_name, x?.to_location_id)],
                ["Units", x?.total_quantity],
                ["Expected arrival", dateText(x?.expected_arrival_date)],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <Grid item xs={6}>
                    <Typography fontSize={14} fontFamily="poppins" fontWeight={600}>
                      {label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography fontSize={14} fontFamily="Poppins" color="var(--text-secondary)" noWrap>
                      : {value === 0 ? 0 : value || "--"}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
            <Box mt={2} justifyContent={"flex-end"} display="flex">
              <Button size="small" onClick={() => handleView(x)}>
                View
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <div style={{ minWidth: "100%" }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} pb={1}>
        <Tooltip title={syncDisabledReason || "Pull the latest transfer orders from Pipe17"}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={onSync}
              disabled={syncing || Boolean(syncDisabledReason)}
              sx={{ textTransform: "none", fontFamily: "Poppins", fontSize: "12px" }}
            >
              {syncing ? "Syncing…" : "Sync from Pipe17"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? "60vh" : `calc(100vh - 250px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {isMobile() ? (
          <MobileCardView />
        ) : (
          <TableContainer sx={{ overflowY: "auto", paddingInline: 0.4, maxHeight: "100%" }}>
            <Table stickyHeader aria-label="transfer orders table" sx={{ border: "1px solid var(--border-color)" }}>
              <TableHead>
                <StyledTableRow>
                  {TableSchema.map((column) => (
                    <TableHeader
                      key={column.key}
                      column={column}
                      filterData={filterData}
                      filterKey={column.filterKey}
                      handleOpenFilter={column.handleOpenFilter}
                      title={column.title}
                    />
                  ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <StyledTableRow>
                    <TableCell colSpan={TableSchema.length} align="center">
                      <Box sx={{ py: 5 }}>
                        <Typography fontFamily="Poppins" fontWeight={600} fontSize="15px">
                          No transfer orders yet
                        </Typography>
                        <Typography fontFamily="Poppins" fontSize="13px" color="var(--text-secondary)">
                          Use Sync from Pipe17 to pull them in.
                        </Typography>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ) : (
                  data.map((x, rowIndex) => (
                    <StyledTableRow key={x?._id || rowIndex}>
                      {TableSchema.map((column, colIndex) => (
                        <StyledTableCell key={column.key || colIndex} sx={{ width: column.width }}>
                          {column.render ? column.render(x, rowIndex) : "--"}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= (page + 1) * (filterData?.limit || 15)}
      />

      <Popover
        open={Boolean(anchorElPopover)}
        anchorEl={anchorElPopover}
        onClose={() => setAnchorElPopover(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ "& .MuiPaper-root": { boxShadow: "none", border: "1px solid var(--border-color)", borderRadius: "4px" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", padding: "10px" }}>
          <Button style={{ justifyContent: "flex-start", textTransform: "none" }} onClick={() => handleView(selected)}>
            <Box display="flex" alignItems="center" width="100%" gap={1}>
              <ViewIcon />
              <Typography fontFamily={"poppins"} fontSize={"16px"} color={"var(--text-tertiary)"} fontWeight={500}>
                View
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>

      <TransferOrderViewModal open={openViewModal} onClose={() => setOpenViewModal(false)} transfer={selected} />

      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedfilterData?.options}
          selectedValues={selectedfilterData?.filter}
          onSave={handleSaveFilter}
          currentSortOrder={selectedfilterData?.sort}
          filterKey={selectedfilterData?.key}
          hasMore={selectedfilterData?.loadMoreButton}
          displayKey={selectedfilterData?.displayKey}
          id={selectedfilterData?.id}
          fetchApi={selectedfilterData?.fetchApi}
          searchKey={selectedfilterData?.searchKey}
          type={selectedfilterData?.type}
          placeholderKey={selectedfilterData?.placeholderKey}
        />
      </Popover>
    </div>
  );
};

export default TransferOrders;
